import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { STATIC_STRINGS } from "@asez/shared";
import {
  StepFourState,
  Timing,
  TimingDuration,
  TimingStartEnd,
} from "../types";
import { calculateEndTime, getStartTime } from "../../utils";

const initialState: StepFourState = {
  scheduleSetting: STATIC_STRINGS.TIME_PERIOD,
  timing: {
    startTime: getStartTime(),
    endTime: calculateEndTime(getStartTime()),
  },
  proctoring: true,
  selectedQuestions: [
    { name: STATIC_STRINGS.PHONE_NUMBER, include: false },
    { name: STATIC_STRINGS.LINKEDIN, include: false },
    { name: STATIC_STRINGS.GITHUB, include: false },
    { name: STATIC_STRINGS.CURRENT_LOCATION, include: false },
    { name: STATIC_STRINGS.HOW_SOON, include: false },
    { name: STATIC_STRINGS.CURRENT_CTC, include: false },
    { name: STATIC_STRINGS.EXPECTED_CTC, include: false },
  ],
  customQuestions: [],
};

const stepFourSlice = createSlice({
  name: "stepFour",
  initialState,
  reducers: {
    updateScheduleSetting: (
      state,
      action: PayloadAction<
        typeof STATIC_STRINGS.TIME_PERIOD | typeof STATIC_STRINGS.DURATION
      >
    ) => {
      state.scheduleSetting = action.payload;
      if (state.scheduleSetting === STATIC_STRINGS.TIME_PERIOD) {
        state.timing = {
          startTime:
            (state.timing as TimingStartEnd).startTime ?? getStartTime(),
          endTime:
            (state.timing as TimingStartEnd).endTime ??
            calculateEndTime(getStartTime()),
        };
      } else {
        state.timing = {
          unit: (state.timing as TimingDuration).unit ?? STATIC_STRINGS.HOURS,
          duration: (state.timing as TimingDuration).duration ?? 1,
        };
      }
    },
    updateTiming: (state, action: PayloadAction<Timing>) => {
      if (state.scheduleSetting === STATIC_STRINGS.TIME_PERIOD) {
        const timing = action.payload as TimingStartEnd;
        state.timing = {
          startTime: timing.startTime,
          endTime: timing.endTime,
        };
      } else {
        const timing = action.payload as TimingDuration;
        state.timing = {
          unit: timing.unit,
          duration: timing.duration,
        };
      }
    },
    updateSelectedQuestion: (
      state,
      action: PayloadAction<{ name: string; include: boolean; index: number }>
    ) => {
      state.selectedQuestions[action.payload.index] = {
        name: action.payload.name,
        include: action.payload.include,
      };
    },
    updateProctoring: (state, action: PayloadAction<boolean>) => {
      state.proctoring = action.payload;
    },
    updateCustomQuestions: (
      state,
      action: PayloadAction<{
        question: string;
        questionType: string;
        include: boolean;
      }>
    ) => {
      const { question, questionType, include } = action.payload;

      const existingQuestion = state.customQuestions.find(
        (q) => q.question === question
      );

      if (existingQuestion) {
        existingQuestion.include = include;
      } else {
        state.customQuestions.push({ question, questionType, include });
      }
    },
    resetStepFour: () => initialState,
  },
});

export const {
  updateScheduleSetting,
  updateTiming,
  updateProctoring,
  updateSelectedQuestion,
  updateCustomQuestions,
  resetStepFour,
} = stepFourSlice.actions;
export default stepFourSlice.reducer;
